/* eslint-disable max-statements */
import { SERVICE, requirejsOne } from '@ds/swymkit/tools'
import type { TagNavigator } from '@ds/swymkit/typings'
import type { EventBusListener } from '@vueuse/core'

import { getPlatformId } from '~/features-shared/config-manager/stores/swym-config'
import { useTenantStore } from '~/features-shared/config-manager/stores/tenant'
import { getRefineParams, setRefineParams } from '~/features-shared/tags/composables/filters'
import type { ITaggable, ITaggerFilter } from '~/features-shared/tags/interfaces/tags'
import { areFiltersSame, reformatFilters } from '~/features-shared/tags/utils/6wpanel'

const PROXY_OPTIONS = {
  'ds6w:community': {
    relevant: true,
    expanded: true,
    weight: 65,
  },
  'ds6w:type': {
    relevant: true,
    expanded: true,
    weight: 60,
  },
  'ds6w:created': {
    relevant: true,
    expanded: true,
    weight: 55,
  },
  'ds6w:responsible': {
    relevant: true,
    expanded: true,
    weight: 50,
  },
}

export interface IProxyOptions {
  contextId?: string
  widgetId?: string
  proxyDisplayName?: string
  filteringMode: string
  pod: '6WTags'
  events?: {
    addTagCreationListener: (tagObj: Record<string, string>) => void
    addTagDeletionListener: (tagObj: Record<string, string>) => void
    addFilterChangeListener: (filter: ITaggerFilter) => void
  }
}

export interface IProxyManager {
  id: string
  clearLocalFilters: () => void
  destroy: () => void
  activateProxy: () => void
  deactivateProxy: () => void
  setClient: (newCLient: ITaggable) => void
}

export interface IProxyManagerOptions {
  id: string
  communityUri?: string
  persistTags?: boolean
  proxyBasicOptions?: any
  onDeactivateProxy: () => void
  onActivateProxy: () => void
}

type Predicate = string
interface FilterListenerPayload {
  mode: 'add' | 'set' | 'remove' | 'replace'
  filter: {
    [k: Predicate]: {
      object: string
      type: string
    }[]
  }
}

type FilterListener = EventBusListener<unknown, FilterListenerPayload>

export async function useTagsProxy(initClient: ITaggable, options: IProxyManagerOptions): Promise<IProxyManager> {
  const TagsNavigator = await requirejsOne<TagNavigator>('DS/TagNavigatorProxy/TagNavigatorProxy')
  const { tenantOptions } = useTenantStore()
  const bus = useEventBus('swym-app')
  const id = options.id
  const isProxyActive = ref(false)
  const persistTags = ref(true)
  const lastRefinementData: ITaggerFilter = reactive<ITaggerFilter>({
    allfilters: {},
    localfilters: {},
  })
  const activeFilters: ITaggerFilter = reactive<ITaggerFilter>({
    allfilters: {},
    localfilters: {},
  })
  const lastQuery = ref('')
  const proxyOptions = reactive<IProxyOptions>({
    contextId: undefined,
    widgetId: undefined,
    proxyDisplayName: SERVICE.SWYM,
    filteringMode: TagsNavigator.filteringMode.FilteringOnServer,
    pod: '6WTags',
  })
  const POINTERS = shallowReactive<{
    proxy: any
    client: ITaggable | null
  }>({
    proxy: null,
    client: null,
  })

  /**
   * Proxy is in tagging mode when we are in single content view (detailed/edition)
   */
  const isTaggingModeEnabled = computed(() => {
    return POINTERS.client?.enableTagging === true
  })

  /**
   * Listener that applies filter to the tagger proxy
   *
   * @param event The event name. This listener only handles 'filter-request' event
   * @param payload The payload containing the filter
   * @param payload.mode The mode of setting the filter
   * @param payload.filter The filter that is to be applied
   */
  const filterRequestListener: FilterListener = async (event: unknown, payload?: FilterListenerPayload) => {
    if (event !== 'filter-request' || !payload)
      return

    if (payload.mode === 'add') {
      POINTERS.proxy.addLocalFilter(payload.filter)
    }
    else if (payload.mode === 'set') {
      POINTERS.proxy.clearLocalFilter()
      POINTERS.proxy.setLocalFilter(payload.filter)
    }
    else if (payload.mode === 'replace') {
      // Get the applied filters
      const currentFilters = POINTERS.proxy.getCurrentFilter()

      // Replace only the sent filters and keep others intact
      Object
        .keys(payload.filter)
        .forEach((filterName) => {
          currentFilters.localfilters[filterName] = payload.filter[filterName]
        })

      // Set the updated
      POINTERS.proxy.clearLocalFilter()
      POINTERS.proxy.setLocalFilter(currentFilters.localfilters)
    }
    else {
      POINTERS.proxy.removeLocalFilter(payload.filter)
    }
  }

  /**
   * Focus the proxy on the subject of the content. It disables filtering and enables creating/deleting tags
   *
   * @returns false if proxy "shouldn't" be focused, else the return value of focusOnSubjects method of proxy
   */
  const focus = async () => {
    const sixwURIs = []
    const proxy = POINTERS.proxy
    const client = POINTERS.client

    if (
      proxy
      && proxy.get('isAlive') !== false
      && tenantOptions?.activate_explicit_tags_thru_server !== false
    ) {
      // FOCUS REQUESTED = TRUE
      if (client) {
        const sixwURI = toValue(client.sixwURI) as string

        sixwURIs.push(sixwURI)

        if (
          sixwURIs.length
          && isTaggingModeEnabled.value
          && isProxyActive.value
        ) {
          return proxy.focusOnSubjects(sixwURIs, persistTags.value)
        }
      }
    }

    return false
  }

  /**
   * Remove any focus set on any subject
   *
   * @returns false if unfocus is not needed, else the returned value from unfocus method of the proxy
   */
  const unfocus = async () => {
    const proxy = POINTERS.proxy
    if (proxy && proxy.get('isAlive') !== false) {
      // this._focusRequested = false;
      return proxy.unfocus()
    }
    return false
  }

  /**
   * Callback to be called after proxy is activated.
   * It may call the passed callback "onActivateProxy" if it is passed in options
   */
  const onActivateProxy = async () => {
    await focus()
    options.onActivateProxy?.()
    bus.on(filterRequestListener)
  }

  /**
   * Callback to be called after proxy is deactivated.
   * It may call the passed callback "onDeactivateProxy" if it is passed in options
   */
  const onDeactivateProxy = async () => {
    await unfocus()
    options.onDeactivateProxy?.()
    bus.off(filterRequestListener)
  }

  /**
   * Callback called when a tag is added to a content. It calls the client's addTag method
   *
   * @param tagObj Tag received from the tagger
   */
  const onTagCreation = (tagObj: Record<string, string>) => {
    const proxy = POINTERS.proxy
    const client = POINTERS.client

    if (
      isTaggingModeEnabled.value
      && client
      && proxy
      && proxy.get('isAlive') !== false
    ) {
      client.addTag?.(tagObj, persistTags.value)
    }
  }

  /**
   * Callback called when a tag is deleted from a content. It calls the client's removeTag method
   *
   * @param tagObj Tag received from the tagger
   */
  const onTagDeletion = (tagObj: Record<string, string>) => {
    const proxy = POINTERS.proxy
    const client = POINTERS.client

    if (
      isTaggingModeEnabled.value
      && client
      && proxy
      && proxy.get('isAlive') !== false
    ) {
      client.removeTag?.(tagObj, persistTags.value)
    }
  }

  /**
   * Callback function triggered after on filter change from tagger
   *
   * @param filter Filter object received from tagger
   */
  // eslint-disable-next-line complexity
  const onFilterChange = async (filter: ITaggerFilter, options: Record<string, string | boolean> = {}) => {
    const proxy = POINTERS.proxy
    const client = POINTERS.client

    // If proxy is de-activated or no client is set, reset tagger
    if (!isProxyActive.value || !client)
      return await proxy.setTags({}, [])

    let validQuery
    if ('query' in options)
      validQuery = options.query || null

    else
      validQuery = lastQuery.value

    const validRefinementData = filter || lastRefinementData || {}

    // If no change in query or refine, reset tagger
    if (
      options.bypassChangeCheck !== true
      && validQuery === lastQuery.value
      && areFiltersSame(validRefinementData.allfilters, lastRefinementData.allfilters)
    ) {
      return await proxy.setTags({}, [])
    }

    lastQuery.value = validQuery as string
    lastRefinementData.allfilters = validRefinementData.allfilters
    lastRefinementData.localfilters = validRefinementData.localfilters
    activeFilters.allfilters = filter.allfilters
    activeFilters.localfilters = filter.localfilters

    if (proxy && proxy.get('isAlive') !== false) {
      // If the context is that of a content
      if (isTaggingModeEnabled.value)
        return await proxy.setTags({}, [])

      if (proxyOptions.filteringMode === TagsNavigator.filteringMode.FilteringOnServer) {
        const { queryPart, refineParam } = reformatFilters(filter)
        setRefineParams(id, { refine: refineParam, query: queryPart, filter })
      }
    }
  }

  // Function that creates a TagNavigator proxy
  const init = () => {
    POINTERS.client = initClient
    persistTags.value = options.persistTags || true

    if (options.proxyBasicOptions) {
      proxyOptions.contextId = options.proxyBasicOptions.contextId
      proxyOptions.widgetId = options.proxyBasicOptions.widgetId
    }

    // Callbacks configuration
    proxyOptions.events = {
      addFilterChangeListener: onFilterChange,
      addTagCreationListener: onTagCreation,
      addTagDeletionListener: onTagDeletion,
    }

    // tag_server_url is usually undefined, but we need this config
    const serverUrls = {
      cStorageService: tenantOptions?.config.tenant.tag_server_url,
      taggerService: tenantOptions?.config.tenant.tag_server_url,
      tenant: getPlatformId(),
    }
    POINTERS.proxy = TagsNavigator.createProxy({ ...proxyOptions, ...serverUrls })
    POINTERS.proxy.configurePredicates?.({ ...PROXY_OPTIONS })

    const waitingRefineParams = getRefineParams(id)

    if (
      waitingRefineParams?.filter
      && Object.keys(waitingRefineParams.filter.localfilters || {}).length
    ) {
      POINTERS.proxy.setLocalFilter(waitingRefineParams?.filter.localfilters)
    }
  }

  const clearLocalFilters = () => { }

  const destroy = () => { }

  /**
   * Method called to activate the proxy
   */
  const activateProxy = () => {
    if (isProxyActive.value !== true) {
      POINTERS.proxy.activate()
      isProxyActive.value = true
      onActivateProxy()
    }

    // Set Tags
    POINTERS.proxy.setTags(
      toValue(POINTERS.client?.tagsWithSubjectURIData || {}),
      toValue(POINTERS.client?.tagsWithoutSubjectURIData || []),
    )
  }

  /**
   * Method called to deactivate the proxy
   */
  const deactivateProxy = () => {
    if (isProxyActive.value === true) {
      POINTERS.proxy.deactivate()
      isProxyActive.value = false
      onDeactivateProxy()
    }
  }

  /**
   * Method that is called whenever we want to update the proxy with our tags
   * @returns void
   */
  const onStoreChange = async () => {
    // Do not proceed if the proxy is inactive. There may only be one active proxy but
    // the client may be shared with multiple proxies
    if (!isProxyActive.value)
      return

    const proxy = POINTERS.proxy
    if (proxy && proxy.get('isAlive') !== false) {
      await proxy.setTags(
        toValue(POINTERS.client?.tagsWithSubjectURIData || {}),
        toValue(POINTERS.client?.tagsWithoutSubjectURIData || []),
      )
      focus()
    }
  }

  // Proceed to initialize
  init()

  /**
   * Should be called to update the client. We should update client when the store should be changed
   * but the "context" of tagger shouldn't. E.g. when we change from large view to timeline view
   *
   * @param newClient The new client that should be set
   */
  const setClient = (newClient: ITaggable | null) => {
    POINTERS.client = newClient
    POINTERS.client?.resetTaggingSession?.()
  }

  whenever(() => POINTERS.client, onStoreChange)
  whenever(() => POINTERS.client?.tagsWithSubjectURIData, onStoreChange, { immediate: true, deep: true })
  whenever(() => POINTERS.client?.tagsWithoutSubjectURIData, onStoreChange, { immediate: true, deep: true })

  return {
    id,
    clearLocalFilters,
    destroy,
    activateProxy,
    deactivateProxy,
    setClient,
  }
}
